import { onBeforeUnmount, onMounted, ref } from 'vue';

import { Logger } from '../services/logger';
import { addBroadcastMessageListener } from './broadcast.helpers';
import { IdleTimer, useIdleTimeout } from './idle.helpers';

const logger = Logger.createScoped('UpdateChecker');

export class UpdateChecker {
    private static localVersion = import.meta.env.VITE_APP_VERSION;
    static subscribers = new Set<() => void>();
    static isUpdateAvailable = false;

    private static pollTimer: ReturnType<typeof setInterval> | null = null;
    private static reloadBlockers = new Set<() => boolean>();

    /** Register a check that returns true while reloading would lose something (a live call, an open modal). */
    static registerReloadBlocker(isBlocking: () => boolean) {
        this.reloadBlockers.add(isBlocking);
    }

    static canReloadNow() {
        return ![...this.reloadBlockers].some(isBlocking => isBlocking());
    }

    static setup() {
        logger.info(`👋 from ZynoSuite ${this.localVersion}`);
        addBroadcastMessageListener<string>('fetchedLatestVersion', v => this.processLatestVersion(v));
    }

    /**
     * Poll /version.json in-page so update detection works WITHOUT a service worker (e.g. the
     * embedded CX app, which ships no SW). The full app gets the same signal from the SW's
     * background-check broadcast; this is an independent, SW-free source feeding the same
     * UpdateChecker. Idempotent — safe to call on every screen mount.
     */
    static startVersionPolling(intervalMs = 60_000) {
        if (this.pollTimer) return;
        const check = () => void this.checkNow();
        this.pollTimer = setInterval(check, intervalMs);
        check();
    }

    /** Fetch /version.json once, now. Resolves whether an update is available. */
    static async checkNow(): Promise<boolean> {
        try {
            const response = await fetch('/version.json', { cache: 'no-store', signal: AbortSignal.timeout(5_000) });
            if (response.ok) {
                const version = await response.json();
                if (typeof version === 'string' && /^\d+\.\d+\.\d+/.test(version)) {
                    this.processLatestVersion(version);
                }
            }
        } catch {
            // transient network failure — the caller (or the next poll) retries
        }
        return this.isUpdateAvailable;
    }

    private static processLatestVersion(latestVersion: string) {
        // if (!this.localVersion || this.localVersion === '0.0.0') return;
        if (isVersionGreater(latestVersion, this.localVersion)) {
            logger.info(`🎉 ZynoSuite ${latestVersion} update is available.`);
            this.isUpdateAvailable = true;
            for (const sub of this.subscribers) sub();
        }
    }
}

function isVersionGreater(maybeNewer: string, base: string) {
    const maybeNewerInts = maybeNewer.split('.').map(Number);
    const baseInts = base.split('.').map(Number);
    const len = Math.max(maybeNewerInts.length, baseInts.length);
    for (let i = 0; i < len; i++) {
        const newerPart = maybeNewerInts[i] ?? 0;
        const basePart = baseInts[i] ?? 0;
        if (newerPart > basePart) return true;
        if (newerPart < basePart) return false;
    }
    return false;
}

export function useUpdateNotifier(onUpdateAvailable: () => void) {
    onMounted(() => {
        UpdateChecker.subscribers.add(onUpdateAvailable);
    });
    onBeforeUnmount(() => {
        UpdateChecker.subscribers.delete(onUpdateAvailable);
    });
}

export function useUpdateAvailableRef() {
    const retRef = ref(UpdateChecker.isUpdateAvailable);
    useUpdateNotifier(() => {
        retRef.value = true;
    });
    return retRef;
}

export function useIdleUpdateNotifier(timeoutMs: number, onIdleWithUpdates: () => void) {
    const checkConditions = () => {
        if (UpdateChecker.isUpdateAvailable && IdleTimer.getIdleMs() >= timeoutMs) {
            onIdleWithUpdates();
        }
    };
    useUpdateNotifier(checkConditions);
    useIdleTimeout({ timeoutMs, onIdle: checkConditions });
}

UpdateChecker.setup();
